// Runtime configuration resolved at serve time. The SPA ships as static
// assets and runs under ANY customer hostname, so it hardcodes no origin, API
// base (docs/09-frontend-surface.md §3, §8). The serving
// layer MAY inject overrides on `window.__MARKER_CONFIG__` when it returns
// index.html; absent that, everything is origin-relative.

export interface RuntimeHelpLinks {
  readonly apiReferenceUrl?: string;
  readonly documentationUrl?: string;
  readonly supportUrl?: string;
}

export interface RuntimeSentryConfig {
  // Sentry DSN for the `marker-web` project (ADR-0003). Absent/empty => Sentry
  // is OFF: air-gap never injects a DSN, and self_managed_connected customers
  // opt in with their own. The DSN is injected at serve time, never bundled.
  readonly dsn: string;
  // Deployment environment (MARKER_ENVIRONMENT), install profile
  // (MARKER_INSTALL_PROFILE), and release (MARKER_VERSION), injected to match
  // the backend so frontend and backend events correlate.
  readonly environment: string;
  readonly installProfile: string;
  readonly release: string;
}

export interface RuntimeConfig {
  // Origin-relative base for the generated client. "" means same origin; the
  // client reaches `/v1/...` relatively (never an absolute origin).
  readonly apiBaseUrl: string;
  // Absolute API origin for browser-visible integration guidance. It is not
  // used by the SPA client, which keeps same-origin `/v1` calls relative.
  readonly publicBaseUrl: string;
  // Origin-relative path that begins the API-driven OIDC/SAML redirect. The
  // authoritative path is owned server-side (docs/05-auth-tenancy-machine-access
  // .md); it is injected here, never an absolute origin.
  readonly authLoginPath: string;
  // OPEN(OQ-EXT-43): exact help targets are provided by runtime config.
  readonly helpLinks: RuntimeHelpLinks;
  // Error tracking (ADR-0003). Present only when the serving layer injects a
  // Sentry DSN — hosted on; self_managed_connected customer opt-in; air-gap
  // never injects one. Absent => Sentry stays uninitialized.
  readonly sentry?: RuntimeSentryConfig;
  // The SecretProvider reference `/settings/usage`'s signed-report export
  // sends as `signing_key_ref` (docs/04 §5; docs/08 §XIV — the server
  // resolves it to the installation private key AFTER authorization; no key
  // material ever reaches the SPA). Install-specific, injected at serve time.
  // ASSUMED(default reference name MARKER_USAGE_EXPORT_SIGNING_KEY — the
  // install-specific ref the SPA cannot know has no documented default;
  // a conventional name following the docs/07 §4 secret-reference contract,
  // overridable per install via __MARKER_CONFIG__.)
  readonly usageExportSigningKeyRef: string;
}

declare global {
  interface Window {
    __MARKER_CONFIG__?: unknown;
  }
}

const DEFAULTS: RuntimeConfig = {
  apiBaseUrl: "",
  publicBaseUrl: "",
  authLoginPath: "/auth/login",
  helpLinks: { documentationUrl: "https://docs.usemarker.ai" },
  usageExportSigningKeyRef: "MARKER_USAGE_EXPORT_SIGNING_KEY",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0
    ? value
    : undefined;
}

function sameOriginPath(
  value: unknown,
  allowEmpty: boolean,
): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  if (allowEmpty && value === "") {
    return value;
  }
  return value.startsWith("/") && !value.startsWith("//") ? value : undefined;
}

function parseHelpLinks(value: unknown): RuntimeHelpLinks {
  if (!isRecord(value)) {
    return DEFAULTS.helpLinks;
  }
  return {
    apiReferenceUrl:
      nonEmptyString(value.apiReferenceUrl) ??
      DEFAULTS.helpLinks.apiReferenceUrl,
    documentationUrl:
      nonEmptyString(value.documentationUrl) ??
      DEFAULTS.helpLinks.documentationUrl,
    supportUrl:
      nonEmptyString(value.supportUrl) ?? DEFAULTS.helpLinks.supportUrl,
  };
}

function parseSentryConfig(value: unknown): RuntimeSentryConfig | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const dsn = nonEmptyString(value.dsn);
  const environment = nonEmptyString(value.environment);
  const installProfile = nonEmptyString(value.installProfile);
  const release = nonEmptyString(value.release);
  if (
    dsn === undefined ||
    environment === undefined ||
    installProfile === undefined ||
    release === undefined
  ) {
    return undefined;
  }
  return { dsn, environment, installProfile, release };
}

export function getRuntimeConfig(): RuntimeConfig {
  const injected = window.__MARKER_CONFIG__;
  if (!isRecord(injected)) {
    return DEFAULTS;
  }
  return {
    apiBaseUrl:
      sameOriginPath(injected.apiBaseUrl, true) ?? DEFAULTS.apiBaseUrl,
    publicBaseUrl: nonEmptyString(injected.publicBaseUrl) ?? "",
    authLoginPath:
      sameOriginPath(injected.authLoginPath, false) ?? DEFAULTS.authLoginPath,
    helpLinks: parseHelpLinks(injected.helpLinks),
    sentry: parseSentryConfig(injected.sentry),
    usageExportSigningKeyRef:
      nonEmptyString(injected.usageExportSigningKeyRef) ??
      DEFAULTS.usageExportSigningKeyRef,
  };
}
